import { formatProductLabel } from './productLabel.js';

/**
 * rebuildStandardDataFromJob.js
 *
 * Transforms a full job object (from Redis / GET /api/redis/jobs/full/:jobId)
 * back into the standardData[] shape that CreateWizzardForm expects.
 *
 * Key insight: job.phasePlan is a verbatim copy of the original standard phases,
 * stored at launch time. It already has wire/isolation/diameter specs — everything
 * the wizard needs. We just need to reshape the wrapper.
 *
 * Usage:
 *   const sd = rebuildStandardDataFromJob(job, parentTokenIds, nextPhaseName);
 *   // → pass as standardData prop to CreateWizzardForm
 *   // → pass initialPhaseIndex to start wizard at the right step
 */

/**
 * Returns the index in phasePlan for a given phase name.
 * @param {object[]} phasePlan
 * @param {string} phaseName
 * @returns {number} index, or 0 if not found
 */
export function resolvePhaseIndex(phasePlan, phaseName) {
  if (!phaseName) return 0;
  const idx = (phasePlan || []).findIndex(p => p.name === phaseName);
  return idx === -1 ? 0 : idx;
}

/**
 * Normalises a phase's execution field.
 *
 * In phasePlan stored on the job:
 *  - conductor_isolation → conductors[].execution already is [] (filled or empty)
 *  - rope/interior_sheat/exterior_sheat → execution: {} (empty object when unplanned)
 *
 * CreateWizzardForm expects execution to be an Array for non-conductor phases.
 */
function normalisePhaseExecution(
  phase,
  phaseIndex,
  targetPhaseIndex,
  editMode = false,
  editPhaseName = '',
  reconcileMode = false
) {
  // conductor_isolation conductors each have their own execution array — leave as-is
  if (phase.name === 'conductor_isolation') {
    // Normalize flat structure (FY/MYF single-conductor: wire+isolation on phase)
    // into conductors[] so the wizard can render upstream drum selectors
    if (!Array.isArray(phase.conductors) && phase.wire && phase.isolation) {
      const { wire, isolation, execution, ...rest } = phase;
      return {
        ...rest,
        conductors: [{
          wire: { ...wire },
          isolation: { ...isolation },
          execution: Array.isArray(execution) ? execution : []
        }]
      };
    }

    const conductors = (phase.conductors || []).map(c => ({
      ...c,
      execution: Array.isArray(c.execution) ? c.execution : []
    }));
    return { ...phase, conductors };
  }

  // Reconcile mode: keep EVERY phase's launched executions (all states) so the whole
  // production history is visible and any produced drum can be re-attributed.
  if (reconcileMode) {
    const exec = phase.execution;
    return { ...phase, execution: Array.isArray(exec) ? exec : [] };
  }

  // Edit mode: KEEP the launched executions for the edited phase AND every
  // DOWNSTREAM phase (phaseIndex > targetPhaseIndex), so the user sees the drums
  // that already exist (edited phase → change/split/add; downstream → the drums
  // that SURVIVED the edit, so they can keep them and add more against the new
  // upstream). Without this the execution fell through to the "empty" return below
  // and the existing drums disappeared ("nu am cardul de executie activa").
  if (
    editMode &&
    (phase.name === editPhaseName || phaseIndex > targetPhaseIndex)
  ) {
    const exec = phase.execution;
    return { ...phase, execution: Array.isArray(exec) ? exec : [] };
  }

  // For the phase we are about to plan: start with empty execution so the wizard
  // renders the "add machine / drum" UI fresh.
  if (phaseIndex === targetPhaseIndex) {
    return { ...phase, execution: [] };
  }

  // For phases already done (before targetPhaseIndex): keep execution as-is
  // but ensure it is an Array so the wizard can read it as read-only entries.
  if (phaseIndex < targetPhaseIndex) {
    const exec = phase.execution;
    if (Array.isArray(exec)) return phase;
    // {} → [] (nothing was executed yet, or legacy empty-object)
    if (exec && typeof exec === 'object' && Object.keys(exec).length === 0) {
      return { ...phase, execution: [] };
    }
    // Wrap a single object in an array just in case
    if (exec && typeof exec === 'object') {
      return { ...phase, execution: [exec] };
    }
    return { ...phase, execution: [] };
  }

  // Phases after targetPhaseIndex: not yet planned — empty array
  return { ...phase, execution: [] };
}

/**
 * Main function.
 *
 * @param {object}   job             Full job object from Redis
 * @param {string[]} parentTokenIds  Token IDs selected in ProjectBobinState
 * @param {string}   nextPhaseName   Name of the phase to plan next (e.g. "rope")
 * @returns {object[]}               Array with one item — standardData shape
 */
export function rebuildStandardDataFromJob(
  job,
  parentTokenIds = [],
  nextPhaseName = '',
  opts = {}
) {
  const {
    editMode = false,
    editPhaseName = '',
    // Reconcile mode: keep EVERY phase's launched executions AND tag produced/locked
    // drums across ALL phases (not just one editPhaseName), so the reconcile wizard
    // shows the whole production history and every produced drum can be re-attributed.
    reconcileMode = false
  } = opts || {};
  // Sanitize: ensure all execution fields are arrays (not {} objects)
  const phasePlan = (job.phasePlan || []).map(phase => {
    const p = { ...phase };
    if (p.execution && !Array.isArray(p.execution)) p.execution = [];
    if (p.conductors) {
      p.conductors = p.conductors.map(c => ({
        ...c,
        execution: Array.isArray(c.execution) ? c.execution : []
      }));
    }
    return p;
  });

  // ── Enrich phasePlan execution entries with tokenId ──────────────────
  // Build exec.id → tokenId map from tasks' outputPlan (grouped tasks)
  // and from single-output tasks
  const execIdToTokenId = {};
  const taskToToken = {};
  for (const task of Object.values(job.tasks || {})) {
    if (!task?.taskId) continue;
    // grouped tasks have outputPlan with sourceExecId → tokenId
    if (Array.isArray(task.outputPlan)) {
      for (const op of task.outputPlan) {
        if (op.sourceExecId != null && op.tokenId) {
          execIdToTokenId[op.sourceExecId] = op.tokenId;
        }
      }
    }
    if (Array.isArray(task.outputs) && task.outputs[0]) {
      taskToToken[task.taskId] = task.outputs[0];
    }
  }

  for (const phase of phasePlan) {
    // collect all execution entries for this phase, each remembering the colour
    // of the conductor it belongs to — the order-based fallback below pairs them
    // with tokens, and a pairing that ignores colour hands a green-yellow card
    // the black drum's token.
    const allExecs = [];
    const colourOfExec = new Map();
    if (phase.conductors) {
      for (const cond of phase.conductors) {
        const colour = cond?.isolation?.color ?? cond?.color ?? null;
        for (const exec of cond.execution || []) {
          allExecs.push(exec);
          if (exec) colourOfExec.set(exec, colour);
        }
      }
    }
    if (Array.isArray(phase.execution)) {
      for (const exec of phase.execution) {
        allExecs.push(exec);
        if (exec) colourOfExec.set(exec, exec?.color ?? phase?.color ?? null);
      }
    }

    // 1) enrich via taskId on the exec entry
    for (const exec of allExecs) {
      if (!exec || exec.tokenId) continue;
      if (exec.taskId && taskToToken[exec.taskId]) {
        exec.tokenId = taskToToken[exec.taskId];
      }
    }

    // 2) enrich via exec.id → outputPlan.sourceExecId
    for (const exec of allExecs) {
      if (!exec || exec.tokenId) continue;
      if (exec.id != null && execIdToTokenId[exec.id]) {
        exec.tokenId = execIdToTokenId[exec.id];
      }
    }

    // 3) fallback: match by task order for this phase — WITHIN A COLOUR.
    //
    // The two lists are built in different orders. The executions come out in
    // conductor order; the tokens come out in `phases[].taskIds` order, which is
    // the order the tasks were created and has no reason to agree. On a forked
    // CYABYF 4x1.5 they did not:
    //
    //   execs:    BLUE BLUE BROWN BROWN  GREEN-YELLOW GREEN-YELLOW  BLACK BLACK
    //   taskIds:  BLUE BLUE BROWN BROWN  BLACK        BLACK         GREEN-YELLOW GREEN-YELLOW
    //
    // so "Drum #7 BLACK" was handed the GREEN-YELLOW drum's token and the card
    // described a drum that was not the one it would have consumed. Colour is
    // the one thing both sides know, so it is matched on first and order only
    // decides between drums of the SAME colour.
    const phaseTaskIds = job.phases?.[phase.name]?.taskIds || [];
    const unmatched = allExecs.filter(e => e && !e.tokenId);
    if (unmatched.length > 0 && phaseTaskIds.length > 0) {
      // collect all output tokenIds from the phase's tasks, in order
      const phaseTokenIds = [];
      for (const tid of phaseTaskIds) {
        const t = job.tasks?.[tid];
        if (!t) continue;
        for (const outTid of t.outputs || []) phaseTokenIds.push(outTid);
      }
      // matched execs already have tokenIds; assign remaining in order
      const assignedTokenIds = new Set(allExecs.filter(e => e?.tokenId).map(e => e.tokenId));
      const remaining = phaseTokenIds.filter(tid => !assignedTokenIds.has(tid));

      const key = v => (v == null ? '' : String(v).toUpperCase());
      const take = colour => {
        // A drum of this colour, or — when neither side states one — the next
        // one in order, which is what this did for every phase before.
        const wanted = key(colour);
        let idx = remaining.findIndex(
          tid => key(job.tokens?.[tid]?.color) === wanted
        );
        if (idx === -1 && !wanted) idx = 0;
        return idx === -1 ? null : remaining.splice(idx, 1)[0];
      };

      for (const exec of unmatched) {
        const tokenId = take(colourOfExec.get(exec));
        if (tokenId) exec.tokenId = tokenId;
      }
      // Anything a colour could not claim still gets a drum rather than none:
      // a mislabelled token is worth less than no token at all, but an exec with
      // no token shows no production state whatsoever.
      for (const exec of unmatched) {
        if (exec.tokenId || !remaining.length) continue;
        exec.tokenId = remaining.shift();
      }
    }
  }

  // Edit mode: tag each exec of the edit phase with produced/locked state (from
  // its output token) so the wizard can LOCK produced drums and let the user edit
  // the PENDING ones. The phasePlan execs already carry length/machine/drum.
  if (editMode && (editPhaseName || reconcileMode)) {
    const PRODUCED = ['ACTIVE', 'RUNNING', 'IN_PROGRESS', 'DONE', 'COMPLETED', 'FINISHED'];
    const tagExec = exec => {
      if (!exec) return;
      const tok = exec.tokenId ? job.tokens?.[exec.tokenId] : null;
      const producedLen = tok ? Number(tok.producedLength || 0) : 0;
      const produced =
        producedLen > 0 || PRODUCED.includes(String(tok?.status || '').toUpperCase());
      // Merged drum: shared with another product (mergeQueueKey). Lock it so it
      // can't be changed here — editing it would break the merge partner.
      const merged = !!tok?.mergeQueueKey;
      exec._produced = produced;
      exec._merged = merged;
      exec._mergeQueueKey = tok?.mergeQueueKey || null;
      exec._locked = produced || merged;
      exec._producedLength = producedLen;
      // What has already been changed about this drum and why. Written onto the
      // token itself (redis_dev/helpers/tokenCorrection.js) precisely so that
      // every screen showing a drum can say it, rather than leaving the trace in
      // an activity feed nobody opens while reading a drum.
      exec._corrections = Array.isArray(tok?.corrections) ? tok.corrections : [];
      if (tok) {
        if (exec.plannedLength == null) exec.plannedLength = tok.plannedLength ?? 0;
        if (exec.drum_material == null) exec.drum_material = tok.drum_material ?? null;
        if (exec.drum_diameter == null) exec.drum_diameter = tok.drum_diameter ?? null;
        if (exec.color == null) exec.color = tok.color ?? null;
      }
      // Reconcile: remember the product AND colour this drum was LOADED with. Physical
      // position/colour in the wizard = current; origin ≠ current means it was
      // re-attributed (job) and/or re-coloured (inversion).
      if (reconcileMode) {
        exec._reconcileOrigin = job.jobId;
        exec._reconcileOriginColor = exec.color ?? tok?.color ?? null;
        exec._reconcileOriginWireDiameter =
          exec.wireDiameter ?? tok?.wireDiameter ?? null;
      }
    };
    // Reconcile tags every phase; edit tags only the phase being edited.
    const phasesToTag = reconcileMode
      ? phasePlan
      : phasePlan.filter(p => p?.name === editPhaseName);
    for (const ph of phasesToTag) {
      if (ph?.conductors) {
        for (const cond of ph.conductors)
          for (const exec of cond.execution || []) tagExec(exec);
      }
      for (const exec of ph?.execution || []) tagExec(exec);
    }
  }

  // Determine which phase the wizard should open on
  const targetPhaseIndex = resolvePhaseIndex(phasePlan, nextPhaseName);

  // Rebuild the phases array in the same shape CreateWizzardForm expects.
  // phasePlan entries already have: name, dependsOn, diameter, section,
  // material, density, fillingSection, minRadial, maxRadial, conductors, etc.
  const phases = phasePlan.map((phase, i) =>
    normalisePhaseExecution(
      phase,
      i,
      targetPhaseIndex,
      editMode,
      editPhaseName,
      reconcileMode
    )
  );

  // Derive color from tokens: pick the first token that has one,
  // or fall back to the productKey stored in job.source.
  const tokenList = Object.values(job.tokens || {});
  const colorFromToken = tokenList.find(t => t.color)?.color || '';
  const requestedColor =
    colorFromToken ||
    (job.source?.launch?.productKey || '').split('|')[4] ||
    '';

  // The PRODUCT (sheath/wire) colour — the productKey segment matching a known cable
  // colour (NOT the conductor/token colour). Used for the wizard header colour dot.
  const KNOWN_COLORS = [
    'BLACK', 'BLUE', 'BROWN', 'GREEN-YELLOW', 'GREEN', 'GREY', 'GRAY',
    'GREY 7004', 'RED', 'YELLOW', 'ORANGE', 'WHITE', 'NATUR'
  ];
  const productColor =
    String(job.source?.launch?.productKey || '')
      .split('|')
      .find(s => KNOWN_COLORS.includes(String(s).trim().toUpperCase())) || '';

  return [
    {
      // ── Identity ──────────────────────────────────────────────────────────
      standardId: job.standardId,
      // _id is used as the "launchId" inside the wizard; reuse jobId so
      // the wizard can reference back to the parent job unambiguously.
      _id: job.jobId,
      launchInstanceId: job.jobId,
      launchIndex: 0,

      // ── Product spec ──────────────────────────────────────────────────────
      name: job.name,
      requestedProductName: job.name,
      section: job.section,
      numberOfConductors: job.numberOfConductors,
      hasMixedConductorSections: Boolean(job.hasMixedConductorSections),
      greenYellowSection: job.greenYellowSection ?? null,

      // ── Phases ────────────────────────────────────────────────────────────
      // This is the core: phasePlan IS the standard phases + execution history.
      phases,

      // ── Length / color ────────────────────────────────────────────────────
      requestedLength: job.plannedLength,
      length: job.plannedLength,
      requestedColor,
      productColor,

      // ── Continue-mode metadata (consumed by wizard submit logic) ──────────
      _continueMode: true,
      _editMode: editMode,
      _editPhaseName: editMode ? editPhaseName : '',
      _parentJobId: job.jobId,
      _parentTokenIds: parentTokenIds, // bobins from the previous phase
      _targetPhaseIndex: targetPhaseIndex, // wizard opens here
      _targetPhaseName: nextPhaseName,

      // ── Read-only context (useful for rendering completed steps) ──────────
      _jobProgress: job.phases || {}, // phase-level progress tracking
      _existingTokens: job.tokens || {}, // already-produced bobins
      _productLabel: job.productLabel || formatProductLabel(job)
    }
  ];
}
